import { getRouteApi } from '@tanstack/react-router';
import { orderByOptions, type ProjectsSearch } from '../routing/schemas';
import type { ProjectsListingFilter } from '../pages/project/components/ProjectsCore.queries';

const routeApi = getRouteApi('/_private/_home/project/');

/**
 * The project listing filter for the given search params. The route loader prefetches the listing
 * with it and the page reads it through `useProjectsFilters`, so both land on one query key.
 */
export const projectsListingFilterFromSearch = ({
  search,
  tags,
  sort,
}: Pick<ProjectsSearch, 'search' | 'tags' | 'sort'>): Required<
  Pick<ProjectsListingFilter, 'nameFilter' | 'tagNamesFilter' | 'orderBy'>
> => ({
  nameFilter: search ?? '',
  tagNamesFilter: tags?.split(',').filter(Boolean) ?? [],
  orderBy: sort ?? 'Newest',
});

export const useProjectsFilters = () => {
  const searchParams = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  // Current filter values from the zod-validated search params
  const {
    nameFilter: projectNameFilter,
    tagNamesFilter: projectTagNamesFilter,
    orderBy: sortOption,
  } = projectsListingFilterFromSearch(searchParams);
  const isAnyFilterActive = projectTagNamesFilter.length > 0 || projectNameFilter.length > 0;

  const validSortOptions = [...orderByOptions];

  const handleTagFilterChange = (selectedTags: string[]) => {
    void navigate({
      search: (prev) => ({ ...prev, tags: selectedTags.length > 0 ? selectedTags.join(',') : undefined }),
      replace: true,
    });
  };

  const handleSearchFilterChange = (searchTerm: string) => {
    void navigate({
      search: (prev) => ({ ...prev, search: searchTerm.length > 0 ? searchTerm : undefined }),
      replace: true,
    });
  };

  const handleSortChange = (selectedSort: string) => {
    const validatedSort = orderByOptions.find((option) => option === selectedSort);
    if (validatedSort) {
      void navigate({ search: (prev) => ({ ...prev, sort: validatedSort }), replace: true });
    }
  };

  const clearAllFilters = () => {
    void navigate({ search: (prev) => ({ ...prev, tags: undefined, search: undefined }), replace: true });
  };

  return {
    projectNameFilter,
    projectTagNamesFilter,
    isAnyFilterActive,
    sortOption,
    validSortOptions,
    handleTagFilterChange,
    handleSearchFilterChange,
    handleSortChange,
    clearAllFilters,
  };
};
